import { useParams, Navigate } from "react-router-dom";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { SEOHead } from "@/hooks/useSEO";
import seoConfig from "@/config/seo.json";
import {
  StructuredDataHead,
  LLMMetadataHead,
  organizationSchema,
  createWebPageSchema,
} from "@/hooks/useStructuredData";
import { getSolutionPageBySlug } from "@/data/solutionPages";
import { useLanguage } from "@/hooks/useLanguage";

import MarketingHero from "@/components/landing/marketing/MarketingHero";
import MarketingChallengesSection from "@/components/landing/marketing/MarketingChallengesSection";
import MarketingFeaturesSection from "@/components/landing/marketing/MarketingFeaturesSection";
import MarketingBenefitsSection from "@/components/landing/marketing/MarketingBenefitsSection";
import MarketingIntegrationsSection from "@/components/landing/marketing/MarketingIntegrationsSection";
import FAQMarketingSection from "@/components/landing/marketing/FAQMarketingSection";
import CTASection from "@/components/landing/CTASection";

import VertriebHero from "@/components/landing/vertrieb/VertriebHero";
import VertriebChallengesSection from "@/components/landing/vertrieb/VertriebChallengesSection";
import VertriebFeaturesSection from "@/components/landing/vertrieb/VertriebFeaturesSection";
import VertriebBenefitsSection from "@/components/landing/vertrieb/VertriebBenefitsSection";
import VertriebIntegrationsSection from "@/components/landing/vertrieb/VertriebIntegrationsSection";
import FAQVertriebSection from "@/components/landing/vertrieb/FAQVertriebSection";

import KundenserviceHero from "@/components/landing/kundenservice/KundenserviceHero";
import KundenserviceChallengesSection from "@/components/landing/kundenservice/KundenserviceChallengesSection";
import KundenserviceFeaturesSection from "@/components/landing/kundenservice/KundenserviceFeaturesSection";
import KundenserviceBenefitsSection from "@/components/landing/kundenservice/KundenserviceBenefitsSection";
import KundenserviceIntegrationsSection from "@/components/landing/kundenservice/KundenserviceIntegrationsSection";
import FAQKundenserviceSection from "@/components/landing/kundenservice/FAQKundenserviceSection";

import EcommerceHero from "@/components/landing/ecommerce-solution/EcommerceHero";
import EcommerceChallengesSection from "@/components/landing/ecommerce-solution/EcommerceChallengesSection";
import EcommerceFeaturesSection from "@/components/landing/ecommerce-solution/EcommerceFeaturesSection";
import EcommerceBenefitsSection from "@/components/landing/ecommerce-solution/EcommerceBenefitsSection";
import EcommerceIntegrationsSection from "@/components/landing/ecommerce-solution/EcommerceIntegrationsSection";
import FAQEcommerceSolutionSection from "@/components/landing/ecommerce-solution/FAQEcommerceSolutionSection";


type PageKey = keyof typeof seoConfig.pages;

const SolutionDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const pageData = slug ? getSolutionPageBySlug(slug) : undefined;

  if (!pageData) {
    return <Navigate to="/404" replace />;
  }

  const renderContent = () => {
    switch (pageData.componentSet) {
      case "marketing":
        return (<><MarketingHero /><MarketingChallengesSection /><MarketingFeaturesSection /><MarketingBenefitsSection /><MarketingIntegrationsSection /><FAQMarketingSection /><CTASection /></>);
      case "vertrieb":
        return (<><VertriebHero /><VertriebChallengesSection /><VertriebFeaturesSection /><VertriebBenefitsSection /><VertriebIntegrationsSection /><FAQVertriebSection /><CTASection /></>);
      case "kundenservice":
        return (<><KundenserviceHero /><KundenserviceChallengesSection /><KundenserviceFeaturesSection /><KundenserviceBenefitsSection /><KundenserviceIntegrationsSection /><FAQKundenserviceSection /><CTASection /></>);
      case "ecommerce-solution":
        return (<><EcommerceHero /><EcommerceChallengesSection /><EcommerceFeaturesSection /><EcommerceBenefitsSection /><EcommerceIntegrationsSection /><FAQEcommerceSolutionSection /><CTASection /></>);
      default:
        return <Navigate to="/404" replace />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead page={(pageData.seoKey || "home") as PageKey} locale={language} />
      <StructuredDataHead schemas={[
        organizationSchema,
        createWebPageSchema(
          pageData.title,
          pageData.description,
          `/loesungen/${pageData.slug}`,
          [
            { name: "Home", url: "/" },
            { name: "Lösungen", url: "/" },
            { name: pageData.breadcrumbLabel, url: `/loesungen/${pageData.slug}` },
          ]
        ),
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: pageData.serviceName,
          description: pageData.serviceDescription,
          provider: organizationSchema,
          serviceType: "Marketing Automation",
        },
      ]} />
      <LLMMetadataHead
        title={pageData.title}
        description={pageData.description}
        keywords={pageData.keywords}
        canonicalUrl={pageData.canonicalUrl}
        aiCategory={"SaaS, AI Solution, " + pageData.breadcrumbLabel}
        aiEntityType="Solution Page"
        aiUseCase={pageData.serviceDescription}
      />
      <Header />
      <main>{renderContent()}</main>
      <Footer />
    </div>
  );
};

export default SolutionDetail;
